/**
 * API клиент для Jataka Calculator FastAPI backend
 * Обеспечивает типизированный доступ к всем endpoints
 * Использует Axios с автоматической JWT аутентификацией
 */

import { devLog, devWarn } from '../utils/devLog';
import { AxiosError } from 'axios';
import api from './api';
import {
  JatakaRequest,
  JatakaResponse,
  HealthResponse,
  ApiInfo,
  ApiResult,
  ApiError,
  ApiConfig,
  isApiError,
  MotionBufferResponse,
  MotionCacheStats,
  MotionCacheClearResponse
} from '../models/api';
import type {
  VimsottariDasaRequest,
  VimsottariDasaResponse
} from '../components/common/AstrologyChart/DataSections/data/VimsottariDashaData';
import type {
  DegreeMotionResponse,
  DegreeMotionStatusResponse,
  TimeMotionResponse,
  TimeMotionStatusResponse,
  TimeStepUnit
} from '../types/MotionTypes';
import { getApiConfig } from '../config/api';

/**
 * Класс для взаимодействия с Jataka Calculator API
 */
export class JatakaApiService {
  private config: ApiConfig;

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...getApiConfig(), ...config };
  }

  /**
   * Основной метод для расчета джатака (натальной карты)
   *
   * @param request - Данные для расчета джатака
   * @returns Полный расчет джатака с панчангой и астрономическими данными
   */
  async calculateJataka(request: JatakaRequest): Promise<ApiResult<JatakaResponse>> {
    try {
      this.logRequest('POST /api/v1/jataka', request);

      const response = await api.post<JatakaResponse>('/v1/jataka', request, {
        timeout: this.config.timeout
      });

      // Log planet positions with full precision (position format: rasi|degrees|longitude|speed)
      if (response.data?.graha_positions?.planets) {
        const positions = response.data.graha_positions.planets
          .map(p => {
            const parts = p.position.split('|');
            const longitude = parts[2] || p.position;
            return `${p.short_name}: ${longitude}°`;
          })
          .join(', ');
        this.logResponse(`Jataka: ${positions}`, {
          datetime: request.datetime,
          lat: request.latitude,
          lon: request.longitude
        });
      } else {
        this.logResponse('Jataka calculation successful', response.data);
      }

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Jataka calculation failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Проверка работоспособности API
   *
   * @returns Статус работы API
   */
  async checkHealth(): Promise<ApiResult<HealthResponse>> {
    try {
      this.logRequest('GET /api/v1/health');

      const response = await api.get<HealthResponse>('/v1/health', {
        timeout: this.config.timeout
      });

      this.logResponse('Health check successful', response.data);

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Health check failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Получение информации об API
   *
   * @returns Информация об API и доступных endpoints
   */
  async getApiInfo(): Promise<ApiResult<ApiInfo>> {
    try {
      this.logRequest('GET /api/v1/');

      const response = await api.get<ApiInfo>('/v1/', {
        timeout: this.config.timeout
      });

      this.logResponse('API info retrieved', response.data);

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('API info request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Calculate Vimsottari Dasha periods
   *
   * @param request - Request with chart data
   * @returns Vimsottari Dasha calculation with Mahadasha periods
   */
  async calculateVimsottariDasha(request: VimsottariDasaRequest): Promise<ApiResult<VimsottariDasaResponse>> {
    try {
      this.logRequest('POST /api/v1/dasa/vimsottari', request);

      const response = await api.post<VimsottariDasaResponse>('/v1/dasa/vimsottari', request, {
        timeout: this.config.timeout
      });

      this.logResponse('Vimsottari Dasha calculated', {
        birth_dasa_lord: response.data.birth_info.birth_dasa_lord,
        periods_count: response.data.mahadasa_periods.length,
        calculation_time_ms: response.data.calculation_time_ms
      });

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Vimsottari Dasha calculation failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Получение буфера движения планет для анимации
   *
   * @param params - Параметры запроса буфера движения
   * @returns Буфер данных о движении планет
   */
  async getMotionBuffer(params: {
    startTime: string;
    latitude: number;
    longitude: number;
    timezoneOffset?: number;
    duration?: number;
    interval?: number;
  }): Promise<ApiResult<MotionBufferResponse>> {
    try {
      const {
        startTime,
        latitude,
        longitude,
        timezoneOffset = 0,
        duration = 10,
        interval = 30
      } = params;

      const queryParams = new URLSearchParams({
        start_time: startTime,
        lat: latitude.toString(),
        lon: longitude.toString(),
        timezone_offset: timezoneOffset.toString(),
        duration: duration.toString(),
        interval: interval.toString()
      });

      const endpoint = `/v1/motion-buffer?${queryParams.toString()}`;
      this.logRequest(`GET ${endpoint}`, params);

      const response = await api.get<MotionBufferResponse>(endpoint, {
        timeout: this.config.timeout
      });

      this.logResponse('Motion buffer retrieved', {
        points: response.data.total_points,
        duration: response.data.duration_minutes,
        cached: response.data.cache_info.cached
      });

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Motion buffer request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Получение статистики кэша движения планет
   *
   * @returns Статистика кэша
   */
  async getMotionCacheStats(): Promise<ApiResult<MotionCacheStats>> {
    try {
      this.logRequest('GET /api/v1/motion-cache-stats');

      const response = await api.get<MotionCacheStats>('/v1/motion-cache-stats', {
        timeout: this.config.timeout
      });

      this.logResponse('Motion cache stats retrieved', response.data);

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Motion cache stats request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Очистка кэша движения планет
   *
   * @returns Результат очистки кэша
   */
  async clearMotionCache(): Promise<ApiResult<MotionCacheClearResponse>> {
    try {
      this.logRequest('DELETE /api/v1/motion-cache');

      const response = await api.delete<MotionCacheClearResponse>('/v1/motion-cache', {
        timeout: this.config.timeout
      });

      this.logResponse('Motion cache cleared', response.data);

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Motion cache clear request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Get degree-based motion buffer for planet drag interaction
   *
   * @param params - Request parameters
   * @returns Degree motion buffer with exact degree steps
   */
  async getDegreeMotionBuffer(params: {
    planet: string;
    startTime: string;
    latitude: number;
    longitude: number;
    timezoneOffset: number;
    degreeRange: number;
    degreeStep: number;
    forceRefresh?: boolean;
  }): Promise<ApiResult<DegreeMotionResponse>> {
    try {
      const { planet, startTime, latitude, longitude, timezoneOffset, degreeRange, degreeStep, forceRefresh } = params;

      const queryParams = new URLSearchParams({
        planet,
        start_time: startTime,
        lat: latitude.toString(),
        lon: longitude.toString(),
        timezone_offset: timezoneOffset.toString(),
        degree_range: degreeRange.toString(),
        degree_step: degreeStep.toString()
      });

      // Add force_refresh if true (skip cache, recalculate)
      if (forceRefresh) {
        queryParams.set('force_refresh', 'true');
      }

      const endpoint = `/v1/motion-buffer-degrees?${queryParams.toString()}`;
      this.logRequest(`GET ${endpoint}`, params);

      const response = await api.get<DegreeMotionResponse>(endpoint, {
        timeout: 30000 // 30s for large buffers
      });

      // Log center point with planet positions
      const centerPoint = response.data.data?.[response.data.centerIndex];
      if (centerPoint) {
        this.logResponse(`Degree buffer: ${response.data.totalPoints} points, center[${response.data.centerIndex}] t=${centerPoint.t}`, {
          Lg: centerPoint.Lg,
          Sy: centerPoint.Sy,
          Ch: centerPoint.Ch
        });
      } else {
        this.logResponse('Degree motion buffer retrieved', {
          planet: response.data.planet,
          totalPoints: response.data.totalPoints,
          centerIndex: response.data.centerIndex
        });
      }

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Degree motion buffer request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Poll for degree motion buffer calculation status
   * Used for progressive loading - get new points as they're calculated
   *
   * @param taskId - Task UUID from initial calculating response
   * @param lastIndex - Last received index for incremental updates
   * @returns Status response with new points or complete data
   */
  async getDegreeMotionStatus(
    taskId: string,
    lastIndex: number = 0
  ): Promise<ApiResult<DegreeMotionStatusResponse>> {
    try {
      const endpoint = `/v1/motion-buffer-degrees/status/${taskId}?last_index=${lastIndex}`;
      this.logRequest(`GET ${endpoint}`, { taskId, lastIndex });

      const response = await api.get<DegreeMotionStatusResponse>(endpoint, {
        timeout: 10000 // 10s for status check
      });

      this.logResponse('Degree motion status', {
        status: response.data.status,
        progress: response.data.progress,
        newPoints: response.data.newPoints?.length || 0
      });

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Degree motion status request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  // ===========================================================================
  // Time Motion Buffer API
  // ===========================================================================

  /**
   * Get time-based motion buffer for chart time shifting
   *
   * @param params - Request parameters
   * @returns Time motion buffer with all planet positions at time intervals
   */
  async getTimeMotionBuffer(params: {
    startTime: string;
    latitude: number;
    longitude: number;
    timezoneOffset: number;
    stepUnit: TimeStepUnit;
    stepValue: number;
    range?: number;
    forceRefresh?: boolean;
  }): Promise<ApiResult<TimeMotionResponse>> {
    try {
      const { startTime, latitude, longitude, timezoneOffset, stepUnit, stepValue, range, forceRefresh } = params;

      const queryParams = new URLSearchParams({
        start_time: startTime,
        lat: latitude.toString(),
        lon: longitude.toString(),
        timezone_offset: timezoneOffset.toString(),
        step_unit: stepUnit,
        step_value: stepValue.toString()
      });

      if (range !== undefined) {
        queryParams.set('range', range.toString());
      }

      if (forceRefresh) {
        queryParams.set('force_refresh', 'true');
      }

      const endpoint = `/v1/motion-buffer-time?${queryParams.toString()}`;
      this.logRequest(`POST ${endpoint}`, params);

      const response = await api.post<TimeMotionResponse>(endpoint, null, {
        timeout: 30000 // 30s for large buffers
      });

      // Log center point with planet positions
      const centerPoint = response.data.data?.[response.data.centerIndex];
      if (centerPoint) {
        this.logResponse(`Time buffer: ${response.data.totalPoints} points, center[${response.data.centerIndex}] t=${centerPoint.t}`, {
          Lg: centerPoint.Lg,
          Sy: centerPoint.Sy,
          Ch: centerPoint.Ch
        });
      } else {
        this.logResponse('Time motion buffer retrieved', {
          totalPoints: response.data.totalPoints,
          centerIndex: response.data.centerIndex,
          stepUnit: response.data.stepUnit,
          stepValue: response.data.stepValue
        });
      }

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Time motion buffer request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Poll for time motion buffer calculation status
   * Used for progressive loading - get new points as they're calculated
   *
   * @param taskId - Task UUID from initial calculating response
   * @param lastIndex - Last received index for incremental updates
   * @returns Status response with new points or complete data
   */
  async getTimeMotionStatus(
    taskId: string,
    lastIndex: number = 0
  ): Promise<ApiResult<TimeMotionStatusResponse>> {
    try {
      const endpoint = `/v1/motion-buffer-time/status/${taskId}?last_index=${lastIndex}`;
      this.logRequest(`GET ${endpoint}`, { taskId, lastIndex });

      const response = await api.get<TimeMotionStatusResponse>(endpoint, {
        timeout: 10000 // 10s for status check
      });

      this.logResponse('Time motion status', {
        status: response.data.status,
        progress: response.data.progress,
        newPoints: response.data.newPoints?.length || 0
      });

      return {
        success: true,
        data: response.data,
        status: response.status
      };

    } catch (error) {
      const apiError = this.parseAxiosError(error);
      this.logError('Time motion status request failed', apiError);

      return {
        success: false,
        error: apiError,
        status: this.getErrorStatus(error)
      };
    }
  }

  /**
   * Обновление конфигурации API клиента
   *
   * @param newConfig - Новые настройки конфигурации
   */
  updateConfig(newConfig: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.log('API configuration updated', this.config);
  }

  /**
   * Получение текущей конфигурации
   * 
   * @returns Текущая конфигурация API клиента
   */
  getConfig(): ApiConfig {
    return { ...this.config };
  }

  // ===== PRIVATE METHODS =====

  /**
   * Парсинг ошибки Axios
   */
  private parseAxiosError(error: unknown): ApiError {
    if (error instanceof AxiosError) {
      // Ошибка от backend
      if (error.response) {
        const errorData = error.response.data;

        if (isApiError(errorData)) {
          return errorData;
        }

        return {
          error: 'ServerError',
          message: `HTTP ${error.response.status}: ${error.response.statusText}`,
          details: { response_data: errorData }
        };
      }

      // Ошибка сети
      if (error.request) {
        return {
          error: 'NetworkError',
          message: error.message || 'Network error occurred',
          details: {
            code: error.code,
            message: error.message
          }
        };
      }
    }

    // Неизвестная ошибка
    if (error instanceof Error) {
      return {
        error: 'UnknownError',
        message: error.message,
        details: {
          name: error.name,
          stack: error.stack
        }
      };
    }

    return {
      error: 'UnknownError',
      message: 'An unknown error occurred',
      details: { original_error: error }
    };
  }

  /**
   * Получение статуса ошибки
   */
  private getErrorStatus(error: unknown): number {
    if (error instanceof AxiosError && error.response) {
      return error.response.status;
    }
    return 0; // Network error
  }

  /**
   * Логирование запроса
   */
  private logRequest(endpoint: string, data?: any): void {
    if (this.config.enableLogging) {
      this.log(`🚀 API Request: ${endpoint}`, data);
    }
  }

  /**
   * Логирование успешного ответа
   */
  private logResponse(message: string, data?: any): void {
    if (this.config.enableLogging) {
      this.log(`✅ ${message}`, data);
    }
  }

  /**
   * Логирование ошибки
   */
  private logError(message: string, error: ApiError): void {
    if (this.config.enableLogging) {
      this.log(`❌ ${message}`, error);
    }
  }

  /**
   * Общий метод логирования
   */
  private log(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    devLog(`[${timestamp}] JatakaApiService: ${message}`, data || '');
  }
}

// ===== SINGLETON INSTANCE =====

/**
 * Глобальный экземпляр API сервиса для использования в приложении
 */
export const jatakaApiService = new JatakaApiService();

// ===== UTILITY FUNCTIONS =====

/**
 * Создание нового экземпляра API сервиса с кастомной конфигурацией
 * 
 * @param config - Конфигурация API клиента
 * @returns Новый экземпляр JatakaApiService
 */
export function createJatakaApiService(config: Partial<ApiConfig> = {}): JatakaApiService {
  return new JatakaApiService(config);
}

/**
 * Быстрая проверка доступности API
 * 
 * @param baseUrl - URL API для проверки (опционально)
 * @returns Promise<boolean> - true если API доступен
 */
export async function isApiAvailable(baseUrl?: string): Promise<boolean> {
  const service = baseUrl 
    ? createJatakaApiService({ baseUrl, timeout: 5000 })
    : jatakaApiService;

  const result = await service.checkHealth();
  return result.success;
}

/**
 * Создание запроса для расчета джатака из базовых данных
 * 
 * @param params - Параметры для создания запроса
 * @returns JatakaRequest готовый для отправки в API
 */
export function createJatakaRequest({
  datetime,
  latitude,
  longitude,
  timezoneOffset,
  placeName,
  altitude = 0
}: {
  datetime: string;
  latitude: number;
  longitude: number;
  timezoneOffset: number;
  placeName?: string;
  altitude?: number;
}): JatakaRequest {
  return {
    datetime,
    latitude,
    longitude,
    timezone_offset: timezoneOffset,
    place_name: placeName,
    altitude
  };
}

// ===== MOTION API UTILITY FUNCTIONS =====

/**
 * Получение буфера движения планет с удобным интерфейсом
 * 
 * @param params - Параметры для получения буфера движения
 * @returns Promise<MotionBufferResponse | null> - данные движения или null при ошибке
 */
export async function getMotionBuffer({
  startTime,
  latitude,
  longitude,
  timezoneOffset = 0,
  duration = 10,
  interval = 30,
  apiService = jatakaApiService
}: {
  startTime: string | Date;
  latitude: number;
  longitude: number;
  timezoneOffset?: number;
  duration?: number;
  interval?: number;
  apiService?: JatakaApiService;
}): Promise<MotionBufferResponse | null> {
  // Preserve full precision (backend handles microseconds)
  const startTimeStr = startTime instanceof Date
    ? startTime.toISOString()
    : startTime;

  const result = await apiService.getMotionBuffer({
    startTime: startTimeStr,
    latitude,
    longitude,
    timezoneOffset,
    duration,
    interval
  });

  return result.success ? result.data! : null;
}

/**
 * Быстрая проверка доступности Motion API
 * 
 * @param baseUrl - URL API для проверки (опционально)
 * @returns Promise<boolean> - true если Motion API доступен
 */
export async function isMotionApiAvailable(baseUrl?: string): Promise<boolean> {
  const service = baseUrl 
    ? createJatakaApiService({ baseUrl, timeout: 5000 })
    : jatakaApiService;

  const result = await service.getMotionCacheStats();
  return result.success;
}

/**
 * Создание буфера движения планет для анимации с проверкой времени
 * 
 * @param params - Параметры создания буфера
 * @returns Promise<MotionBufferResponse | null>
 */
export async function createMotionBufferForAnimation({
  centerTime,
  latitude,
  longitude,
  timezoneOffset = 0,
  durationMinutes = 60,
  intervalSeconds = 60,
  apiService = jatakaApiService
}: {
  centerTime: string | Date;
  latitude: number;
  longitude: number;
  timezoneOffset?: number;
  durationMinutes?: number;
  intervalSeconds?: number;
  apiService?: JatakaApiService;
}): Promise<MotionBufferResponse | null> {
  // Создаем время начала за половину продолжительности до центрального времени
  // Preserve full precision (backend handles microseconds)
  const centerDate = centerTime instanceof Date ? centerTime : new Date(centerTime);
  const startDate = new Date(centerDate.getTime() - (durationMinutes * 60 * 1000) / 2);

  return getMotionBuffer({
    startTime: startDate.toISOString(),
    latitude,
    longitude,
    timezoneOffset,
    duration: durationMinutes,
    interval: intervalSeconds,
    apiService
  });
}

/**
 * Извлечение данных конкретной планеты из буфера движения (новый формат API)
 * 
 * @param motionBuffer - Буфер движения от API
 * @param planetName - Название планеты (санскрит)
 * @returns Массив данных движения планеты
 */
export function extractPlanetMotion(
  motionBuffer: MotionBufferResponse,
  planetName: 'Surya' | 'Chandra' | 'Mangala' | 'Budha' | 'Guru' | 'Shukra' | 'Sani' | 'Rahu' | 'Ketu'
): Array<{
  timestamp: string;
  longitude: number;
  speed: number;
}> {
  // Mapping от полных имен к API кодам
  const planetApiMapping: Record<string, string> = {
    'Surya': 'Sy',
    'Chandra': 'Ch', 
    'Mangala': 'Ma',
    'Budha': 'Bu',
    'Guru': 'Gu',
    'Shukra': 'Sk',
    'Sani': 'Sa',
    'Rahu': 'Ra',
    'Ketu': 'Ke'
  };

  const apiKey = planetApiMapping[planetName];
  if (!apiKey) {
    devWarn(`Unknown planet name: ${planetName}`);
    return [];
  }

  return motionBuffer.data
    .map(point => {
      const planetMotionString = (point as any)[apiKey];
      if (!planetMotionString || typeof planetMotionString !== 'string') {
        return null;
      }

      // Парсим строку формата "rasi|degrees|longitude|speed"
      const parts = planetMotionString.split('|');
      if (parts.length !== 4) {
        return null;
      }

      const longitude = parseFloat(parts[2]);
      const speed = parseFloat(parts[3]);

      if (isNaN(longitude) || isNaN(speed)) {
        return null;
      }

      return {
        timestamp: point.timestamp,
        longitude,
        speed
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}